import React from 'react';
import { Callout, Step, DocHeading, DocTable, Shot } from './components';

function PageBag({ navigate }) {
  return (
    <div>
      <h1 className="text-[2.5rem] leading-[1.1] font-semibold text-white tracking-[-0.02em] mb-3">Bag</h1>
      <p className="text-gray-400 leading-relaxed mb-8">
        The Bag tab is where you protect bag slots. A locked slot is never touched by the bot&rsquo;s
        irreversible actions &mdash; selling, melting, and using up a teleport item on a route. There
        is no switch here and nothing runs from this tab: it only decides what is safe. Every
        character has its own locks. Everything is on the{' '}
        <strong className="text-white">FARM &rsaquo; Bag</strong> tab.
      </p>

      <Callout type="info">
        <strong>Why it has its own tab.</strong> The same locks protect items from{' '}
        <button onClick={() => navigate('auto-sell')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">Auto Sell</button>,{' '}
        <button onClick={() => navigate('auto-melt')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">Melt</button>{' '}
        and the{' '}
        <button onClick={() => navigate('autopilot')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">Autopilot</button>,
        so they live in one place instead of on one of those tabs.
      </Callout>

      <DocHeading>Locking a Slot</DocHeading>
      <Step n={1} title="Enter the world with your character">
        The locks belong to a character, so they load when it enters the world. The{' '}
        <strong>Locks of</strong> line names whose locks you are looking at. Until then the card says{' '}
        <em>Waiting for your character - the locks load when you enter the world.</em> and the grid
        is not shown &mdash; and nothing is sold, melted or used from the bag meanwhile.
      </Step>
      <Step n={2} title="Pick a bag tab">
        The numbered buttons at the top of the <strong>PROTECTED SLOTS</strong> card are the pages
        of your main bag (usually 1&ndash;8; the number follows your bag size once it has been read).
        <strong>P</strong> is the premium bag.
      </Step>
      <Step n={3} title="Hover before you click">
        Hovering a slot shows the item in it and whether it is protected:{' '}
        <em>LOCKED - never sold or melted</em> or <em>NOT protected - click to lock</em>.
      </Step>
      <Step n={4} title="Click to lock">
        The slot turns red. Click it again to unlock it. The change takes effect immediately.
      </Step>

      <Shot
        name="bag-01-protected-slots.png"
        tab="FARM › Bag"
        caption="The locks of Tinkerbolt on tab 1: red slots are locked, purple ones hold an item that is NOT protected."
        shoot="menushot (sx_bag: vault::Attach(„Tinkerbolt”), 224 rekesz): a FARM › Bag lap — Locks of Tinkerbolt, az 1. fül, 7 lezárt (piros), lila és üres rekeszek, a színmagyarázat (a narancs „moved out of a locked slot” sorral), alul This tab / Locked."
      />

      <DocHeading>Reading the Grid</DocHeading>
      <DocTable
        headers={['Colour', 'Meaning']}
        rows={[
          [<span className="text-red-400 font-semibold">Red</span>, 'locked - never sold or melted'],
          [<span className="text-orange-400 font-semibold">Orange</span>, 'moved out of a locked slot - still protected. Hover it to see which locked slot it came from.'],
          [<span className="text-brand-300 font-semibold">Purple</span>, 'an item sits here - NOT protected'],
          [<span className="text-gray-300 font-semibold">Dark</span>, 'empty slot'],
          [<span className="text-gray-500 font-semibold">Greyed out</span>, 'This slot does not exist in your bag. It cannot be clicked.'],
        ]}
      />
      <Callout type="warning">
        <strong>Purple is the colour that stands out, and it means NOT protected.</strong> Only red
        (and orange) slots are safe. If in doubt, hover the slot: the second line of the tooltip
        says it plainly.
      </Callout>

      <DocHeading>The Controls</DocHeading>
      <DocTable
        headers={['Control', 'What it does']}
        rows={[
          [<>Numbered tab buttons and <strong className="text-white">P</strong></>, 'Switch the page shown in the grid. Each page is 4 rows × 7 columns = 28 slots. There are as many numbered buttons as your main bag has pages (8 until the bag has been read). If your premium bag has more than one page, they read P1, P2 and so on.'],
          [<strong className="text-white">LOCK THIS TAB</strong>, 'Locks every slot on the page you are looking at.'],
          [<strong className="text-white">UNLOCK THIS TAB</strong>, 'Unlocks every slot on that page.'],
          [<strong className="text-white">UNLOCK ALL</strong>, 'Removes every lock on every tab. It asks first: the button turns into a red REALLY UNLOCK ALL, and only that one does it. It only asks when something is actually locked.'],
          [<><strong className="text-white">Locked</strong> (above the grid)</>, 'How many slots are locked on this page, and in total. A quick check that your locks are where you think they are.'],
          [<strong className="text-white">This tab</strong>, 'Which slots of which bag the page shows, and how many slots that bag has.'],
          [<><strong className="text-white">Locked</strong> (below the grid)</>, 'The total number of locked slots across every tab.'],
        ]}
      />

      <DocHeading>What a Lock Protects</DocHeading>
      <DocTable
        headers={['Bot action', 'With a locked slot']}
        rows={[
          [<button onClick={() => navigate('auto-sell')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">Selling</button>, 'The slot is never even looked at, whatever the filters or item rules say.'],
          [<button onClick={() => navigate('auto-melt')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">Melting</button>, 'Same: nothing is melted from a locked slot, identified gear included.'],
          [<button onClick={() => navigate('autopilot')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">Teleport items on a route</button>, 'A teleport item in a locked slot is never used. If no copy of it sits in an unlocked slot, the route stops and tells you the teleport item is not in an unlocked bag slot. The route check may also name the slot: “bag 0” is the main bag, “bag 1” the premium bag, and slots are counted from 0, so slot 0 is cell 1 on tab 1 and slot 28 is cell 1 on tab 2.'],
        ]}
      />
      <p className="text-sm text-gray-400 mb-4">
        A lock does not lock the item in the game. You can still use, move or sell it yourself, and
        picking up loot is not affected at all.
      </p>
      <Callout type="info">
        <strong>The lock follows the item.</strong> You lock a slot, and the bot remembers which item
        sat in it. If that item moves &mdash; the game&rsquo;s sort button, a drag, a rearrange
        &mdash; it stays protected wherever it lands (orange on the grid) until you unlock the slot it
        came from. Put something new into a locked slot and it is protected too. The card counts
        them: <em>N item(s) sat in a locked slot and are remembered</em>. This does not apply to
        teleport items on a route: for those only the slot counts.
      </Callout>

      <DocHeading>Messages</DocHeading>
      <DocTable
        headers={['Message', 'What it means']}
        rows={[
          [<span className="text-amber-300">Waiting for your character - the locks load when you enter the world.</span>, 'Loading screen or character select. Nothing is sold, melted or used from the bag until the locks are in.'],
          [<strong className="text-white">(bag not read yet)</strong>, 'The bot has not read your bag yet, so the grid does not know what is where. It fills in by itself once a character is in the game.'],
          [<span className="text-amber-300">N locked slot(s) were taken over from the old settings.</span>, 'Shown once per character after updating from an older version: the locks you had before were brought over. Check them and unlock what you do not want protected.'],
          [<span className="text-red-400">The bag lock file of NAME could not be loaded.</span>, <>The file with this character&rsquo;s locks is damaged or cannot be opened. Nothing is sold, melted or used from the bag. If you have a copy of the file, put it back and wait a few seconds; otherwise press <strong>START OVER WITH NO LOCKS</strong>, then <strong>REALLY START OVER</strong>, and lock your slots again. The damaged file is kept.</>],
          [<span className="text-red-400">Your locks could not be saved - selling and melting are stopped until they are.</span>, 'A lock that is not on the disk would not survive a restart. The bot keeps trying to save.'],
          [<span className="text-red-400">Too many items are remembered from locked slots - selling and melting are stopped.</span>, 'Unlock slots you no longer need.'],
          [<span className="text-amber-300">The game reports N slots here, less than 8 tabs x 28.</span>, 'Your main bag has fewer slots than eight full pages. The slots past the end are greyed out. Nothing to fix.'],
          [<span className="text-red-400">This bag has N slots - only the first 256 can be protected. Tell the developer.</span>, 'Your bag is larger than the protection can cover. Keep valuables in the first slots and report it.'],
        ]}
      />

      <DocHeading>Good to Know</DocHeading>
      <DocTable
        headers={['Question', 'Answer']}
        rows={[
          ['Are locks saved?', 'Yes, the moment you click, in a file of their own for each character. A restart, a crash or Auto Restart cannot lose them.'],
          ['Are locks per character?', 'Yes. Every character has its own locks, and two game windows never mix them up. A brand-new character starts with none.'],
          ['Can I keep different locks for different kinds of farming?', <>Yes &mdash; a{' '}<button onClick={() => navigate('farm-profiles')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">farm profile</button>{' '}saves your locks too, and asks before loading would unlock anything.</>],
          ['“It sold something I had locked.”', 'Check the Locks of line first: are these the locks of the character you are playing? Then hover the slot. The lock may be on the other bag (main vs. P), or it was unlocked before the sale. Check the Locked total too: if it reads 0, nothing is locked.'],
        ]}
      />
    </div>
  );
}

export default PageBag;
